import { Component, type ErrorInfo, type ReactNode } from 'react';
import { autoBugReport } from '../observability/console-capture.js';

interface Props { children: ReactNode }
interface State { error: Error | null }

/**
 * App-level error boundary — the last line of defence for render-phase crashes,
 * which window.onerror does NOT see (React swallows them and unmounts the tree,
 * leaving a blank page). Feeds the same auto-bug-report pipeline as the other
 * two capture triggers (console-capture uncaughts + api errors) and renders a
 * minimal recover screen instead of the blank page.
 */
export class RootErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    autoBugReport('render_error', `render crash: ${error.message}`, {
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
    });
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ padding: '4rem 2rem', textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
        <h1 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Something went wrong</h1>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          The error has been reported automatically. Reloading usually recovers.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{ padding: '0.5rem 1.25rem', cursor: 'pointer' }}
        >
          Reload
        </button>
      </div>
    );
  }
}
